import React from 'react';
import {
    Box, Typography, Chip, Alert, Table, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Tooltip,
} from '@mui/material';

const dinheiro = (v) =>
    v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const data = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR') : '—');

/** Número grande com rótulo — os indicadores do topo do painel. */
function Indicador({ rotulo, valor, cor, dica }) {
    const conteudo = (
        <Box sx={{ minWidth: 104 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: cor || 'text.primary', lineHeight: 1.1 }}>
                {valor}
            </Typography>
            <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
        </Box>
    );
    return dica ? <Tooltip title={dica} arrow>{conteudo}</Tooltip> : conteudo;
}

/**
 * Sessões pagas × realizadas do paciente.
 *
 * "Pagas" vem do valor efetivamente recebido nos títulos da assinatura, não de
 * um contador: por isso acompanha baixa parcial. "Realizadas" vem do status das
 * sessões, e não de `sessoesUtilizadas`, que já cai no agendamento.
 */
export default function SaldoSessoesPaciente({ saldo, loading, erro }) {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }
    if (erro) return <Alert severity="error">{erro}</Alert>;
    if (!saldo) return <Alert severity="info">Sem dados de saldo para este paciente.</Alert>;

    const semAssinatura = !saldo.assinaturas || saldo.assinaturas.length === 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                <Indicador rotulo="Contratadas" valor={saldo.totalContratadas} />
                <Indicador rotulo="Pagas" valor={saldo.totalPagas} cor="#1ABB9C"
                    dica="Quantas sessões o valor já recebido cobre" />
                <Indicador rotulo="Realizadas" valor={saldo.totalRealizadas}
                    dica="Atendimentos efetivamente feitos" />
                <Indicador rotulo="Agendadas" valor={saldo.totalAgendadas}
                    dica="Marcadas e ainda não atendidas" />
                <Indicador rotulo="Faltas" valor={saldo.totalFaltas} cor={saldo.totalFaltas > 0 ? '#ed6c02' : undefined} />
                <Indicador rotulo="Canceladas" valor={saldo.totalCanceladas} />
            </Box>

            {saldo.saldoAPagar > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>{saldo.saldoAPagar}</strong>{' '}
                    {saldo.saldoAPagar === 1 ? 'sessão realizada' : 'sessões realizadas'} além do que foi pago.
                </Alert>
            )}
            {saldo.saldoAPagar === 0 && saldo.saldoDisponivel > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <strong>{saldo.saldoDisponivel}</strong>{' '}
                    {saldo.saldoDisponivel === 1 ? 'sessão paga disponível' : 'sessões pagas disponíveis'} para agendar.
                </Alert>
            )}

            {semAssinatura ? (
                <Alert severity="info">Paciente sem assinatura registrada.</Alert>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Plano</strong></TableCell>
                                <TableCell><strong>Vigência</strong></TableCell>
                                <TableCell align="right"><strong>Contratadas</strong></TableCell>
                                <TableCell align="right"><strong>Pagas</strong></TableCell>
                                <TableCell align="right"><strong>Realizadas</strong></TableCell>
                                <TableCell align="right"><strong>Restantes</strong></TableCell>
                                <TableCell align="right"><strong>Valor/sessão</strong></TableCell>
                                <TableCell align="right"><strong>Pago</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {saldo.assinaturas.map((a) => (
                                <TableRow key={a.idAssinatura}>
                                    <TableCell>
                                        {a.nomePlano || '—'}
                                        {a.avulsa && (
                                            <Chip size="small" label="Avulsa" sx={{ ml: 1 }} variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell>{data(a.dataInicio)} – {data(a.dataFim)}</TableCell>
                                    {/* Plano avulso não tem quantidade fixa: nada a contar. */}
                                    <TableCell align="right">{a.avulsa ? '—' : a.sessoesContratadas}</TableCell>
                                    <TableCell align="right">{a.avulsa ? '—' : a.sessoesPagas}</TableCell>
                                    <TableCell align="right">{a.sessoesRealizadas}</TableCell>
                                    <TableCell align="right">{a.avulsa ? '—' : a.sessoesRestantes}</TableCell>
                                    <TableCell align="right">{dinheiro(a.valorPorSessao)}</TableCell>
                                    <TableCell align="right">{dinheiro(a.valorPago)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={a.status || '—'} variant="outlined" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}
